import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useHistory, useLocation, useParams } from 'react-router-dom';

import { doc, getDoc, updateDoc } from 'firebase/firestore';

import Swal from 'sweetalert2';

import { DB } from '~/services/firebase';

import Dashboard from '~/layouts/Dashboard';
import { useToast } from '~/hooks/toast';
import IComplement from '~/interfaces/IComplement';
import getCurrencyFormatted from '~/utils/getCurrencyFormatted';
import { useProductsCompany } from '~/hooks/productsCompany';
import { bff } from '~/services/bff';
import { Status } from '~/components/Status';

interface Params {
  id: string;
}

interface StateProps {
  item: IComplement;
}

const { REACT_APP_FIREBASE_PROJECT } = process.env;

const ComplementItems: React.FC = () => {
  const history = useHistory();
  const location = useLocation<StateProps>();
  const params = useParams<Params>();
  const { addToast } = useToast();
  const { updateComplements } = useProductsCompany();
  const [complement, setComplement] = useState<IComplement>({} as IComplement);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('Todos');
  const [group, setGroup] = useState('Todos');
  const [showFilter, setShowFilter] = useState(false);
  const [showGroupOrderModal, setShowGroupOrderModal] = useState(false);
  const [groupOrders, setGroupOrders] = useState<Record<string, number>>({});
  const [orderedGroups, setOrderedGroups] = useState<string[]>([]);

  const { id } = params;

  const handleChangeStatus = useCallback(
    async (index: number) => {
      const updateItems =
        complement && complement.items ? [...complement.items] : [];

      updateItems[index].status = !updateItems[index].status;

      await updateDoc(doc(DB, 'complements', id), {
        items: updateItems,
      });

      updateComplements({
        ...complement,
        items: updateItems,
      });

      await bff.delete(`/complements/${REACT_APP_FIREBASE_PROJECT}`);
    },
    [complement, id, updateComplements],
  );

  const loadComplement = useCallback(async () => {
    const ref = doc(DB, 'complements', id);
    const docSnap = await getDoc(ref);
    const item = docSnap.data() as IComplement;

    const items = item.items.map(objectItem => ({
      ...objectItem,
      value_formatted: getCurrencyFormatted(parseFloat(objectItem.value)),
    }));

    setComplement({
      ...item,
      uid: id,
      items,
    } as IComplement);
    setGroupOrders(item.groupOrders || {});
  }, [id]);

  useEffect(() => {
    if (location.state && location.state.item) {
      setComplement(location.state.item);
    } else if (id) {
      loadComplement();
    } else history.goBack();
  }, [id, history, loadComplement, location]);

  const filteredItems = useMemo(() => {
    if (!complement || !complement.items) return [];

    const filtered = complement.items.filter(item => {
      let result = true;

      if (status === 'Ativo') {
        result = item.status === true;
      } else if (status === 'Inativo') {
        result = item.status === false;
      }

      if (group !== 'Todos') {
        result = result && item.groupTitle === group;
      }

      result =
        result &&
        item.name.toLocaleLowerCase().includes(search.toLocaleLowerCase());

      return result;
    });
    return filtered;
  }, [search, complement, status, group]);

  const availableGroups = useMemo(() => {
    if (!complement || !complement.items) return [];
    const groups = complement.items
      .map(item => item.groupTitle)
      .filter((g): g is string => !!g);
    return ['Todos', ...Array.from(new Set(groups))];
  }, [complement]);

  const uniqueGroups = useMemo(() => {
    if (!complement || !complement.items) return [];
    const groups = complement.items
      .map(item => item.groupTitle)
      .filter((g): g is string => !!g);
    return Array.from(new Set(groups));
  }, [complement]);

  useEffect(() => {
    if (showGroupOrderModal && uniqueGroups.length > 0 && orderedGroups.length === 0) {
      const sorted = uniqueGroups
        .map(g => ({ name: g, order: groupOrders[g] ?? 0 }))
        .sort((a, b) => a.order - b.order)
        .map(g => g.name);
      setOrderedGroups(sorted);
    }
  }, [showGroupOrderModal, uniqueGroups]);

  const handleSaveGroupOrders = useCallback(async () => {
    const newOrders: Record<string, number> = {};
    orderedGroups.forEach((g, index) => {
      newOrders[g] = index;
    });

    await updateDoc(doc(DB, 'complements', id), {
      groupOrders: newOrders,
    });

    updateComplements({
      ...complement,
      groupOrders: newOrders,
    });

    setGroupOrders(newOrders);

    await bff.delete(`/complements/${REACT_APP_FIREBASE_PROJECT}`);

    addToast({
      type: 'success',
      description: 'Ordem dos grupos salva com sucesso!',
    });

    setShowGroupOrderModal(false);
  }, [complement, orderedGroups, id, updateComplements, addToast]);

  const moveGroup = (group: string, direction: 'up' | 'down') => {
    const currentIndex = orderedGroups.indexOf(group);
    if (currentIndex === -1) return;

    const targetIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
    if (targetIndex < 0 || targetIndex >= orderedGroups.length) return;

    const newOrdered = [...orderedGroups];
    [newOrdered[currentIndex], newOrdered[targetIndex]] = [newOrdered[targetIndex], newOrdered[currentIndex]];

    setOrderedGroups(newOrdered);
  };
  const handleConfirmRemove = useCallback(
    async (uid: string) => {
      const { isConfirmed } = await Swal.fire({
        title: 'Atenção!',
        text: 'Confirme a exclusão do item?',
        icon: 'warning',
        confirmButtonText: 'Sim',
        showCancelButton: true,
        cancelButtonText: 'Não',
      });

      if (isConfirmed) {
        const updateItems = [...complement.items];

        const findIndex = updateItems.findIndex(find => find.uid === uid);

        if (findIndex !== -1) {
          updateItems.splice(findIndex, 1);

          updateItems.sort((a, b) =>
            a.name > b.name ? 1 : b.name > a.name ? -1 : 0,
          );

          await updateDoc(doc(DB, 'complements', id), {
            items: updateItems,
          });

          await bff.delete(`/complements/${REACT_APP_FIREBASE_PROJECT}`);

          const itemUpdated = {
            ...complement,
            items: updateItems,
          };

          setComplement(itemUpdated);

          updateComplements(itemUpdated);

          addToast({
            type: 'success',
            description: 'Item foi removido com sucesso.',
          });
        }
      }
    },
    [addToast, complement, id, updateComplements],
  );

  return (
    <Dashboard containerMessage="col-12">
      <div className="col-12">
        <div className="nk-block-head nk-block-head-sm">
          <div className="nk-block-between">
            <div className="nk-block-head-content">
              <div className="nk-block-head-sub">
                <Link className="back-to" to="/complements">
                  <em className="icon ni ni-arrow-left" />
                  <span>Voltar</span>
                </Link>
              </div>
              <h6 className="nk-block-title">{complement.name}</h6>
            </div>
            <div className="nk-block-head-content">
              <div className="toggle-wrap nk-block-tools-toggle">
                <button
                  type="button"
                  className={`btn btn-icon btn-trigger toggle-expand mr-n1 ${
                    showFilter && 'active'
                  }`}
                  data-target="pageMenu"
                  onClick={() => setShowFilter(current => !current)}
                >
                  <em className="icon ni ni-more-v" />
                </button>
                <div
                  className={`toggle-expand-content ${
                    showFilter && 'expanded'
                  }`}
                  data-content="pageMenu"
                >
                  <ul className="nk-block-tools g-3">
                    <li>
                      <div className="form-control-wrap">
                        <div className="form-icon form-icon-right">
                          <em className="icon ni ni-search" />
                        </div>
                        <input
                          type="text"
                          className="form-control"
                          id="default-04"
                          placeholder="Buscar por item"
                          onChange={event => {
                            setSearch(event.target.value);
                          }}
                        />
                      </div>
                    </li>
                    <li>
                      <div className="drodown">
                        <Link
                          to="#status"
                          className="dropdown-toggle dropdown-indicator btn btn-outline-light btn-white"
                          data-toggle="dropdown"
                        >
                          {status}
                        </Link>
                        <div className="dropdown-menu dropdown-menu-right">
                          <ul className="link-list-opt no-bdr">
                            <li>
                              <Link
                                to="#status"
                                onClick={() => setStatus('Todos')}
                              >
                                <span>Todos</span>
                              </Link>
                            </li>
                            <li>
                              <Link
                                to="#active"
                                onClick={() => setStatus('Ativo')}
                              >
                                <span>Ativo</span>
                              </Link>
                            </li>
                            <li>
                              <Link
                                to="#inactive"
                                onClick={() => setStatus('Inativo')}
                              >
                                <span>Inativo</span>
                              </Link>
                            </li>
                          </ul>
                        </div>
                      </div>
                    </li>
                    <li>
                      <div className="drodown">
                        <Link
                          to="#group"
                          className="dropdown-toggle dropdown-indicator btn btn-outline-light btn-white"
                          data-toggle="dropdown"
                        >
                          {group === 'Todos' ? 'Todos os grupos' : group}
                        </Link>
                        <div className="dropdown-menu dropdown-menu-right">
                          <ul className="link-list-opt no-bdr">
                            {availableGroups.map(g => (
                              <li key={g}>
                                <Link
                                  to="#group"
                                  onClick={() => setGroup(g)}
                                >
                                  <span>{g === 'Todos' ? 'Todos os grupos' : g}</span>
                                </Link>
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    </li>
                  </ul>
                  <ul className="nk-block-tools g-3 mt-2 justify-content-end">
                    <li>
                      <button
                        type="button"
                        className="btn btn-outline-light btn-white"
                        onClick={() => setShowGroupOrderModal(true)}
                      >
                        <em className="icon ni ni-sort" />
                        <span>Ordenar Grupos</span>
                      </button>
                    </li>
                    <li className="nk-block-tools-opt">
                      <Link
                        to={{
                          pathname: `/complements/items/${id}/create`,
                          state: {
                            item: complement,
                          },
                        }}
                        data-target="addProduct"
                        className="toggle btn btn-icon btn-primary d-md-none"
                      >
                        <em className="icon ni ni-plus" />
                      </Link>
                      <Link
                        to={{
                          pathname: `/complements/items/${id}/create`,
                          state: {
                            item: complement,
                          },
                        }}
                        data-target="addProduct"
                        className="toggle btn btn-primary d-none d-md-inline-flex"
                      >
                        <em className="icon ni ni-plus" />
                        <span>Criar Item</span>
                      </Link>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="nk-block">
          <div className="card card-bordered">
            <div className="card-inner-group">
              <div className="card-inner p-0">
                <div className="nk-tb-list">
                  <div className="nk-tb-item nk-tb-head">
                    <div className="nk-tb-col">
                      <span>Nome</span>
                    </div>
                    <div className="nk-tb-col">
                      <span>Grupo</span>
                    </div>
                    <div className="nk-tb-col">
                      <span>Valor</span>
                    </div>
                    <div className="nk-tb-col">
                      <span>Status</span>
                    </div>

                    <div className="nk-tb-col nk-tb-col-tools">
                      <ul className="nk-tb-actions gx-1 my-n1">
                        <li className="mr-n1" />
                      </ul>
                    </div>
                  </div>

                  {filteredItems.map((item, index) => (
                    <div
                      className="nk-tb-item"
                      key={item.uid}
                      style={{ cursor: 'pointer' }}
                      onClick={() => history.push(`/complements/items/${id}/edit/${item.uid}`)}
                    >
                      <div className="nk-tb-col">
                        <span className="title">{item.name}</span>
                      </div>

                      <div className="nk-tb-col">
                        <span className="title">
                          {item.groupTitle || '-'}
                        </span>
                      </div>

                      <div className="nk-tb-col">
                        <div className="asterisk tb-asterisk">
                          <span className="fs-14px">
                            {getCurrencyFormatted(Number(item.value))}
                          </span>
                        </div>
                      </div>
                      <div className="nk-tb-col">
                        <Status
                          value={item.status}
                          onChange={() => handleChangeStatus(index)}
                        />
                      </div>
                      <div className="nk-tb-col nk-tb-col-tools">
                        <ul className="nk-tb-actions gx-1 my-n1">
                          <li className="mr-n1">
                            <div className="dropdown">
                              <Link
                                to="#options"
                                className="dropdown-toggle btn btn-icon btn-trigger"
                                data-toggle="dropdown"
                                onClick={e => e.stopPropagation()}
                              >
                                <em className="icon ni ni-more-h" />
                              </Link>
                              <div className="dropdown-menu dropdown-menu-right">
                                <ul className="link-list-opt no-bdr">
                                  <li>
                                    <Link
                                      to={`/complements/items/${id}/edit/${item.uid}`}
                                    >
                                      <em className="icon ni ni-edit" />
                                      <span>Editar</span>
                                    </Link>
                                  </li>

                                  <li>
                                    <Link
                                      to="#remove"
                                      onClick={() => {
                                        handleConfirmRemove(item.uid);
                                      }}
                                    >
                                      <em className="icon ni ni-trash" />
                                      <span>Excluir</span>
                                    </Link>
                                  </li>
                                </ul>
                              </div>
                            </div>
                          </li>
                        </ul>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {showGroupOrderModal && (
        <div className="modal fade show" style={{ display: 'block' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Ordenar Grupos</h5>
                <button
                  type="button"
                  className="close"
                  onClick={() => setShowGroupOrderModal(false)}
                >
                  <span>&times;</span>
                </button>
              </div>
              <div className="modal-body">
                {orderedGroups.length === 0 ? (
                  <p className="text-muted">Nenhum grupo encontrado.</p>
                ) : (
                  <ul className="list-group">
                    {orderedGroups.map((g, index) => (
                        <li
                          key={g}
                          className="list-group-item d-flex justify-content-between align-items-center"
                        >
                          <span>{g}</span>
                          <div>
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-secondary mr-1"
                              disabled={index === 0}
                              onClick={() => moveGroup(g, 'up')}
                            >
                              <em className="icon ni ni-arrow-up" />
                            </button>
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-secondary"
                              disabled={index === orderedGroups.length - 1}
                              onClick={() => moveGroup(g, 'down')}
                            >
                              <em className="icon ni ni-arrow-down" />
                            </button>
                          </div>
                        </li>
                      ))}
                  </ul>
                )}
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowGroupOrderModal(false)}
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleSaveGroupOrders}
                >
                  Salvar Ordem
                </button>
              </div>
            </div>
          </div>
          <div
            className="modal-backdrop fade show"
            onClick={() => setShowGroupOrderModal(false)}
          />
        </div>
      )}
    </Dashboard>
  );
};

export default ComplementItems;
